/**
 * FilterPanel コンポーネント
 *
 * ドロワー形式のフィルタUI。
 * シンプルな3種類のフィルタを提供:
 * - ジャンル（schema:genre）: 8種類
 * - 発表形態: 同期元CSVの発表形式
 * - キーワード: 同期元CSVのキーワード列
 * - グループ: 同期元CSVのグループ列
 */

import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { Button } from '@/components/atoms/Button';
import { FilterSection } from '@/components/molecules/FilterSection';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useI18n } from '@/i18n/useI18n';
import { getGenreBackgroundClass } from '@/lib/genre-colors';

const EMPTY_STRING_ARRAY: string[] = [];

/** 選択状態のみを管理するローカル状態 */
interface LocalFilterState {
  genres: string[];
  artforms: string[];
  keywords: string[];
  groups: string[];
  dateRange: [number, number];
}

/**
 * FilterPanelのフィルタ値
 */
export interface FilterPanelValue {
  genres: string[];
  artforms: string[];
  keywords: string[];
  groups: string[];
  dateRange: [number, number];
}

/**
 * FilterPanel コンポーネントのProps
 */
export interface FilterPanelProps {
  /** ドロワーの開閉状態 */
  isOpen: boolean;

  /** ドロワーを閉じるコールバック */
  onClose: () => void;

  /** 現在のフィルタ値 */
  value: FilterPanelValue;

  /** フィルタ適用時のコールバック */
  onApply: (value: FilterPanelValue) => void;

  /** リセット時のコールバック（親の既定値へ戻す） */
  onReset: () => void;

  /** 利用可能なジャンル一覧 */
  availableGenres: string[];

  /** 利用可能な発表形態一覧 */
  availableArtforms?: string[];

  /** 利用可能なキーワード一覧 */
  availableKeywords?: string[];

  /** 利用可能なグループ一覧 */
  availableGroups?: string[];

  /** 日付範囲の最小値・最大値 */
  dateRangeLimit: [number, number];
}

/** トグルハンドラファクトリ */
function createToggleHandler(
  setState: Dispatch<SetStateAction<LocalFilterState>>,
  field: keyof Pick<
    LocalFilterState,
    'genres' | 'artforms' | 'keywords' | 'groups'
  >
) {
  return (item: string) => {
    setState((prev) => {
      const current = prev[field];
      const next = current.includes(item)
        ? current.filter((x) => x !== item)
        : [...current, item];
      return { ...prev, [field]: next };
    });
  };
}

/** 汎用フィルタボタンクラス（ジャンル以外） */
function defaultButtonClass(_item: string, isSelected: boolean): string {
  return isSelected
    ? 'bg-primary text-primary-foreground'
    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80';
}

/**
 * FilterPanel コンポーネント
 *
 * 使用例:
 * ```tsx
 * <FilterPanel
 *   isOpen={isFilterOpen}
 *   onClose={() => setIsFilterOpen(false)}
 *   value={filters}
 *   onApply={setFilters}
 *   onReset={resetFilters}
 *   availableGenres={['アニメーション', 'イラストレーション', 'マンガ']}
 *   availableArtforms={['書籍', '雑誌', '絵本']}
 *   availableKeywords={['おすすめ', 'CM', 'MV']}
 *   dateRangeLimit={[1960, 2024]}
 * />
 * ```
 */
export function FilterPanel({
  isOpen,
  onClose,
  value,
  onApply,
  onReset,
  availableGenres,
  availableArtforms = EMPTY_STRING_ARRAY,
  availableKeywords = EMPTY_STRING_ARRAY,
  availableGroups = EMPTY_STRING_ARRAY,
  dateRangeLimit,
}: FilterPanelProps) {
  const { t } = useI18n();
  const drawerRef = useFocusTrap<HTMLDivElement>({
    isActive: isOpen,
    onEscape: onClose,
  });

  // ローカル状態（適用前の一時的な状態）を1オブジェクトで管理
  const [local, setLocal] = useState<LocalFilterState>({
    genres: value.genres,
    artforms: value.artforms,
    keywords: value.keywords,
    groups: value.groups,
    dateRange: value.dateRange,
  });

  // valueが変更されたらローカル状態に反映
  useEffect(() => {
    setLocal({
      genres: value.genres,
      artforms: value.artforms,
      keywords: value.keywords,
      groups: value.groups,
      dateRange: value.dateRange,
    });
  }, [
    value.genres,
    value.artforms,
    value.keywords,
    value.groups,
    value.dateRange,
  ]);

  const handleGenreToggle = createToggleHandler(setLocal, 'genres');
  const handleArtformToggle = createToggleHandler(setLocal, 'artforms');
  const handleKeywordToggle = createToggleHandler(setLocal, 'keywords');
  const handleGroupToggle = createToggleHandler(setLocal, 'groups');

  // フィルタを適用
  const applyFilters = () => {
    onApply({
      genres: local.genres,
      artforms: local.artforms,
      keywords: local.keywords,
      groups: local.groups,
      dateRange: local.dateRange,
    });
    onClose();
  };

  // フィルタをリセット
  const handleReset = () => {
    onReset();
    setLocal({
      genres: value.genres,
      artforms: value.artforms,
      keywords: value.keywords,
      groups: value.groups,
      dateRange: value.dateRange,
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* オーバーレイ */}
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />

      {/* ドロワー */}
      <div
        ref={drawerRef}
        className="fixed inset-y-0 right-0 z-50 w-full max-w-md overflow-y-auto overscroll-contain bg-background shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-panel-title"
      >
        <div className="flex h-full flex-col">
          {/* ヘッダー */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h2 id="filter-panel-title" className="text-xl font-bold">
              {t('filter.title')}
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              {t('common.close')}
            </Button>
          </div>

          {/* コンテンツ */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <FilterSection
              titleId="genre-filter-title"
              title={t('filter.genre')}
              items={availableGenres}
              selectedItems={local.genres}
              onToggle={handleGenreToggle}
              getButtonClass={getGenreBackgroundClass}
            />

            <FilterSection
              titleId="artform-filter-title"
              title={t('filter.artform')}
              items={availableArtforms}
              selectedItems={local.artforms}
              onToggle={handleArtformToggle}
              getButtonClass={defaultButtonClass}
            />

            <FilterSection
              titleId="group-filter-title"
              title={t('filter.group')}
              items={availableGroups}
              selectedItems={local.groups}
              onToggle={handleGroupToggle}
              getButtonClass={defaultButtonClass}
            />

            <FilterSection
              titleId="keyword-filter-title"
              title={t('filter.keyword')}
              items={availableKeywords}
              selectedItems={local.keywords}
              onToggle={handleKeywordToggle}
              getButtonClass={defaultButtonClass}
            />

            {/* 制作年範囲フィルタ */}
            <div className="mb-6">
              <h3 className="mb-3 font-semibold">{t('filter.yearCreated')}</h3>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="year-start"
                    className="mb-1 block text-sm text-muted-foreground"
                  >
                    {t('filter.yearFrom')}
                  </label>
                  <input
                    id="year-start"
                    type="range"
                    min={dateRangeLimit[0]}
                    max={dateRangeLimit[1]}
                    value={local.dateRange[0]}
                    onChange={(e) =>
                      setLocal((prev) => ({
                        ...prev,
                        dateRange: [
                          parseInt(e.target.value),
                          prev.dateRange[1],
                        ],
                      }))
                    }
                    className="w-full"
                  />
                  <div className="text-sm font-medium">
                    {local.dateRange[0]}
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="year-end"
                    className="mb-1 block text-sm text-muted-foreground"
                  >
                    {t('filter.yearTo')}
                  </label>
                  <input
                    id="year-end"
                    type="range"
                    min={dateRangeLimit[0]}
                    max={dateRangeLimit[1]}
                    value={local.dateRange[1]}
                    onChange={(e) =>
                      setLocal((prev) => ({
                        ...prev,
                        dateRange: [
                          prev.dateRange[0],
                          parseInt(e.target.value),
                        ],
                      }))
                    }
                    className="w-full"
                  />
                  <div className="text-sm font-medium">
                    {local.dateRange[1]}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* フッター */}
          <div className="border-t px-6 py-4">
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleReset} fullWidth>
                {t('common.reset')}
              </Button>
              <Button variant="primary" onClick={applyFilters} fullWidth>
                {t('common.apply')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
